import { useAppState, type PanelName } from './AppState';
import { navigate, useRoute } from './router';
import { PeriodicTablePanel } from './panels/PeriodicTablePanel';
import { LibraryPanel } from './panels/LibraryPanel';
import { WhiteboardPanel } from './panels/WhiteboardPanel';
import { CalculatorPanel } from './panels/CalculatorPanel';
import { HomeScreen } from './screens/HomeScreen';
import { AddClassScreen } from './screens/AddClassScreen';
import { StudyScreen } from './screens/StudyScreen';
import { QuizScreen } from './screens/QuizScreen';
import { SearchScreen } from './screens/SearchScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { SessionScreen } from './screens/SessionScreen';
import { NotebookScreen } from './screens/NotebookScreen';
import { WeakSpotsScreen } from './screens/WeakSpotsScreen';
import { ExamScreen } from './screens/ExamScreen';
import { MidtermScreen } from './screens/MidtermScreen';
import { CheatSheetScreen } from './screens/CheatSheetScreen';
import { VocabularyScreen } from './screens/VocabularyScreen';
import { ProblemsScreen } from './screens/ProblemsScreen';
import { SigFigToolScreen } from './screens/SigFigToolScreen';
import { BalancerScreen } from './screens/BalancerScreen';
import { SyncIndicator } from './components/SyncIndicator';
import { UpdatePrompt } from './components/UpdatePrompt';
import { PanelAsSheet } from './panels/Panel';

const NAV = [
  { path: '/', label: 'Home', icon: '⌂' },
  { path: '/study', label: 'Cards', icon: '▤' },
  { path: '/quiz', label: 'Quiz', icon: '✓' },
  { path: '/weak', label: 'Progress', icon: '◔' },
  { path: '/search', label: 'Search', icon: '⌕' },
];

function Screen({ path }: { path: string }) {
  switch (path) {
    case '/add':
      return <AddClassScreen />;
    case '/study':
      return <StudyScreen />;
    case '/quiz':
      return <QuizScreen />;
    case '/search':
      return <SearchScreen />;
    case '/settings':
      return <SettingsScreen />;
    case '/session':
      return <SessionScreen />;
    case '/notebook':
      return <NotebookScreen />;
    case '/weak':
      return <WeakSpotsScreen />;
    case '/midterm':
      return <MidtermScreen />;
    case '/exam':
      return <ExamScreen />;
    case '/sheet':
      return <CheatSheetScreen />;
    case '/vocab':
      return <VocabularyScreen />;
    case '/problems':
      return <ProblemsScreen />;
    case '/sigfig':
      return <SigFigToolScreen />;
    case '/balance':
      return <BalancerScreen />;
    default:
      return <HomeScreen />;
  }
}

export function App() {
  const { panel, setPanel, overlayPanel, setOverlayPanel, ready, toastMessage, examInProgress, local } =
    useAppState();
  const route = useRoute();

  // A docked panel takes its strip of the screen from the shell, so the shell
  // has to know: the page is laid out in what is left, and nothing is covered.
  const docked = panel !== null && (local?.panelsDocked ?? true);

  /**
   * One toolbar button, three meanings.
   *
   * With nothing open, it opens that panel where panels go. With it already
   * open, it closes it. With a *different* panel docked, it opens over the
   * page instead of replacing what is docked — so the table can be checked
   * without losing the document being read, and closing it puts you back.
   */
  function togglePanel(name: Exclude<PanelName, null>) {
    if (overlayPanel === name) return setOverlayPanel(null);
    if (panel === name) return setPanel(null);
    if (panel !== null && docked) return setOverlayPanel(name);
    setPanel(name);
  }

  const isOpen = (name: Exclude<PanelName, null>) => panel === name || overlayPanel === name;

  // The Library is shut during an exam by default, because his real exams do
  // not allow notes. The periodic table stays, because they provide one.
  const libraryLocked = examInProgress && !local?.allowLibraryInExams;

  return (
    <div
      className={`app${docked ? ' app--docked' : ''}`}
      style={
        local?.dockSize ? ({ '--dock-size': local.dockSize } as React.CSSProperties) : undefined
      }
    >
      <header className="toolbar print-hide">
        <button className="toolbar__brand" onClick={() => navigate('/')}>
          Chem Bank
        </button>
        <div className="toolbar__actions">
          <SyncIndicator />
          <button
            className="icon-btn"
            aria-pressed={isOpen('periodic')}
            aria-label="Periodic table"
            title="Periodic table"
            onClick={() => togglePanel('periodic')}
          >
            ⚛
          </button>
          <button
            className="icon-btn"
            aria-pressed={isOpen('whiteboard')}
            aria-label="Whiteboard"
            title="Whiteboard"
            onClick={() => togglePanel('whiteboard')}
          >
            ✎
          </button>
          <button
            className="icon-btn"
            aria-pressed={isOpen('calculator')}
            aria-label="Calculator"
            title="Calculator"
            onClick={() => togglePanel('calculator')}
          >
            √
          </button>
          <button
            className="icon-btn"
            aria-pressed={isOpen('library')}
            aria-label="Library"
            title={libraryLocked ? 'Library is off during exams' : 'Library'}
            disabled={libraryLocked}
            onClick={() => togglePanel('library')}
          >
            📚
          </button>
          <button
            className="icon-btn"
            aria-label="Settings"
            title="Settings"
            aria-pressed={route.path === '/settings'}
            onClick={() => navigate('/settings')}
          >
            ⚙
          </button>
        </div>
      </header>

      <nav className="nav print-hide" aria-label="Main">
        {NAV.map((item) => (
          <button
            key={item.path}
            className="nav__item"
            aria-current={route.path === item.path ? 'page' : undefined}
            onClick={() => navigate(item.path)}
          >
            <span className="nav__icon" aria-hidden="true">
              {item.icon}
            </span>
            {item.label}
          </button>
        ))}
      </nav>

      <main className="main">{ready ? <Screen path={route.path} /> : <p className="muted">Loading…</p>}</main>

      {/* The table, the calculator and the whiteboard all stay open during an
          exam: his own exams allow a calculator and scratch paper, and they
          hand out the table. Only the Library closes. */}
      {panel === 'periodic' ? <PeriodicTablePanel onClose={() => setPanel(null)} /> : null}
      {panel === 'library' ? <LibraryPanel onClose={() => setPanel(null)} /> : null}
      {panel === 'calculator' ? <CalculatorPanel onClose={() => setPanel(null)} /> : null}
      {panel === 'whiteboard' ? <WhiteboardPanel onClose={() => setPanel(null)} /> : null}

      {/* The second panel, over the page. The docked one is still underneath. */}
      {overlayPanel ? (
        <PanelAsSheet>
          {overlayPanel === 'periodic' ? <PeriodicTablePanel onClose={() => setOverlayPanel(null)} /> : null}
          {overlayPanel === 'library' ? <LibraryPanel onClose={() => setOverlayPanel(null)} /> : null}
          {overlayPanel === 'calculator' ? <CalculatorPanel onClose={() => setOverlayPanel(null)} /> : null}
          {overlayPanel === 'whiteboard' ? <WhiteboardPanel onClose={() => setOverlayPanel(null)} /> : null}
        </PanelAsSheet>
      ) : null}

      <UpdatePrompt />

      {toastMessage ? (
        <div
          role="status"
          style={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 'calc(var(--tap) + var(--safe-bottom) + 16px)',
            background: 'var(--text)',
            color: 'var(--bg)',
            padding: '10px 16px',
            borderRadius: 'var(--r-full)',
            zIndex: 70,
            fontSize: '0.9rem',
            maxWidth: '90vw',
            textAlign: 'center',
          }}
        >
          {toastMessage}
        </div>
      ) : null}
    </div>
  );
}
